/**
 * Affiliate Phase B2 — public config + portal state RPCs (thin server-fn wrappers).
 *
 * These are the ONLY portal reads exposed to the browser. They project through
 * SECURITY DEFINER Postgres functions so:
 *   - `affiliate_public_config` returns feature flag + non-sensitive tier /
 *     pricing / minimum-payout data (safe for footer gating + terms preview).
 *   - `affiliate_portal_state` returns exactly one enumerated state string plus
 *     the current active terms version. Never returns admin notes, risk flags
 *     or rejection reasons — those are structurally excluded server-side.
 *
 * When the affiliate feature flag is off:
 *   - `getAffiliatePublicConfig` returns `{ enabled: false }` only.
 *   - `getAffiliatePortalState` returns `state: "disabled"` from the RPC.
 */
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type AffiliatePublicConfig =
  | { enabled: false }
  | {
      enabled: true;
      customerDiscountBps: number;
      tiers: Array<{ min: number; max: number | null; bps: number }>;
      minimumPayoutPaise: number;
      policyVersion: string;
      settlementDays: number;
      returnWindowDays: number;
    };

/** Public, no-auth. Reads the projection RPC through the publishable client. */
export const getAffiliatePublicConfig = createServerFn({ method: "GET" }).handler(
  async (): Promise<AffiliatePublicConfig> => {
    const url = process.env.SUPABASE_URL;
    const key = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!url || !key) return { enabled: false };
    const client = createClient<Database>(url, key, {
      auth: { persistSession: false, autoRefreshToken: false, storage: undefined },
      global: {
        fetch: (input, init) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) {
            h.delete("Authorization");
          }
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });
    const rpc = client.rpc.bind(client) as unknown as (
      n: string,
    ) => Promise<{
      data: Array<{
        feature_enabled: boolean;
        customer_discount_bps: number;
        commission_tiers: Array<{ min: number; max: number | null; bps: number }>;
        minimum_payout_paise: number;
        policy_version: string;
        settlement_days: number | null;
        return_window_days: number | null;
      }> | null;
      error: { message: string } | null;
    }>;
    const { data, error } = await rpc("affiliate_public_config");
    if (error || !data || data.length === 0) return { enabled: false };
    const row = data[0];
    if (!row.feature_enabled) return { enabled: false };
    return {
      enabled: true,
      customerDiscountBps: row.customer_discount_bps,
      tiers: Array.isArray(row.commission_tiers) ? row.commission_tiers : [],
      minimumPayoutPaise: row.minimum_payout_paise,
      policyVersion: row.policy_version,
      settlementDays: Number(row.settlement_days ?? 45),
      returnWindowDays: Number(row.return_window_days ?? 3),
    };
  },
);

export type AffiliatePortalState = {
  state:
    | "not_authenticated"
    | "disabled"
    | "not_applied"
    | "pending"
    | "approved_unbound"
    | "approved_bound"
    | "rejected"
    | "paused"
    | "reconsent_required";
  affiliateId: string | null;
  needsReconsent: boolean;
  currentTermsVersion: string | null;
};

/** Auth-required portal state router. Generic on error to avoid enumeration. */
export const getAffiliatePortalState = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AffiliatePortalState> => {
    try {
      const rpc = (context.supabase.rpc.bind(context.supabase)) as unknown as (
        n: string,
      ) => Promise<{
        data: Array<{
          state: string;
          affiliate_id: string | null;
          needs_reconsent: boolean;
          current_terms_version: string | null;
        }> | null;
        error: { message: string } | null;
      }>;
      const { data, error } = await rpc("affiliate_portal_state");
      if (error || !data || data.length === 0) {
        return {
          state: "not_applied",
          affiliateId: null,
          needsReconsent: false,
          currentTermsVersion: null,
        };
      }
      const row = data[0];
      return {
        state: (row.state as AffiliatePortalState["state"]) ?? "not_applied",
        affiliateId: row.affiliate_id ?? null,
        needsReconsent: Boolean(row.needs_reconsent),
        currentTermsVersion: row.current_terms_version ?? null,
      };
    } catch {
      return {
        state: "not_applied",
        affiliateId: null,
        needsReconsent: false,
        currentTermsVersion: null,
      };
    }
  });
